/**
 * useUserPref — key/value preferences ISOLATE PER UTENTE.
 *
 * Sprint isolamento multi-account:
 *   • Autenticato → `time-inside:pref:user:<userId>:<key>` in localStorage
 *                    + `payload.prefs.<key>` in MongoDB via /api/user-data.
 *   • Anonimo     → `time-inside:pref:anon:<key>` in localStorage.
 *   • Al cambio utente, lo stato in memoria viene azzerato al default e
 *     ricaricato dalla cache scoped-user o dal cloud.
 *   • NESSUN auto-upload della preferenza locale verso il cloud del nuovo
 *     utente quando il cloud non ha ancora quella preferenza — un nuovo
 *     account eredita SEMPRE i valori di default.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { fetchUserData, patchUserData } from "./userDataApi";

function lsKey(userId, key) {
  const scope = userId ? `user:${userId}` : "anon";
  return `time-inside:pref:${scope}:${key}`;
}

function readLocal(userId, key, defaultValue) {
  try {
    const raw = window.localStorage.getItem(lsKey(userId, key));
    if (raw == null) return defaultValue;
    return JSON.parse(raw);
  } catch {
    return defaultValue;
  }
}

function writeLocal(userId, key, value) {
  try {
    window.localStorage.setItem(lsKey(userId, key), JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

export function useUserPref(key, defaultValue) {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const userId = isAuthenticated && user && user.id ? user.id : null;
  const [value, setValueInternal] = useState(defaultValue);
  const [ready, setReady] = useState(false);
  const isMountedRef = useRef(true);
  const currentUserRef = useRef(userId);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Reset immediato al cambio utente (login/logout/switch)
  useEffect(() => {
    if (currentUserRef.current !== userId) {
      currentUserRef.current = userId;
      setReady(false);
      setValueInternal(readLocal(userId, key, defaultValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Hydrate from cloud when logged in
  useEffect(() => {
    if (authLoading) return;
    const uidAtFetch = userId;
    if (!isAuthenticated) {
      setValueInternal(readLocal(null, key, defaultValue));
      setReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cloud = await fetchUserData();
        if (cancelled) return;
        if (currentUserRef.current !== uidAtFetch) return;
        const prefs = (cloud && cloud.prefs) || {};
        const remote = prefs[key];
        if (remote !== undefined) {
          setValueInternal(remote);
          writeLocal(uidAtFetch, key, remote);
        } else {
          // Nessun valore cloud: NON facciamo più auto-upload della cache
          // locale — un nuovo utente eredita SEMPRE il defaultValue, mai la
          // preferenza appartenuta ad un altro account su questo device.
          setValueInternal(defaultValue);
          writeLocal(uidAtFetch, key, defaultValue);
        }
      } catch {
        /* offline → keep last value (in-memory) */
      } finally {
        if (!cancelled && isMountedRef.current
            && currentUserRef.current === uidAtFetch) {
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading, key, userId]);

  const setValue = useCallback(
    async (next) => {
      const v = typeof next === "function" ? next(value) : next;
      setValueInternal(v);
      writeLocal(userId, key, v);
      if (isAuthenticated) {
        try {
          const cloud = await fetchUserData();
          if (currentUserRef.current !== userId) return; // utente cambiato
          const prefs = (cloud && cloud.prefs) || {};
          await patchUserData("prefs", { ...prefs, [key]: v });
        } catch {
          /* offline → local only */
        }
      }
    },
    [value, key, isAuthenticated, userId]
  );

  return [value, setValue, ready];
}
